import React, { useRef, useEffect, useState, useCallback } from 'react';
import { StoneColor, Coordinate } from '@go-platform/go-engine';
import './GoBoard.css';

/** 讲解标记类型 */
export type MarkType = 'circle' | 'triangle' | 'cross' | 'square' | 'letter';

/** 讲解标记 */
export interface BoardMark {
  coord: Coordinate;
  type: MarkType;
  letter?: string;
}

interface GoBoardProps {
  size?: 9 | 13 | 19;
  stones?: StoneColor[][];
  onMove?: (coord: Coordinate) => void;
  disabled?: boolean;
  showCoordinates?: boolean;
  highlightLastMove?: boolean;
  lastMove?: Coordinate | null;
  showHover?: boolean;
  marks?: BoardMark[];
  deadStones?: Coordinate[];
  blockOccupied?: boolean;
  cursor?: Coordinate | null;
  cursorMode?: boolean;
  onCursor?: (coord: Coordinate) => void;
  fill?: boolean;
}

const GoBoard: React.FC<GoBoardProps> = ({
  size = 19,
  stones = [],
  onMove,
  disabled = false,
  showCoordinates = true,
  highlightLastMove = true,
  lastMove = null,
  showHover = true,
  marks = [],
  deadStones = [],
  blockOccupied = false,
  cursor = null,
  cursorMode = false,
  onCursor,
  fill = false
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoverCoord, setHoverCoord] = useState<Coordinate | null>(null);
  const [boardSize, setBoardSize] = useState(600);

  // 初始化棋盘状态
  const initializeStones = (): StoneColor[][] => {
    if (stones.length === size) {
      return stones;
    }

    const newStones: StoneColor[][] = [];
    for (let i = 0; i < size; i++) {
      newStones[i] = new Array(size).fill(StoneColor.EMPTY);
    }
    return newStones;
  };

  const [boardStones, setBoardStones] = useState<StoneColor[][]>(initializeStones);

  // 更新棋盘大小
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateBoardSize = () => {
      const w = el.clientWidth;
      if (fill) {
        // 填充模式：按容器宽高取小者，棋盘自适应剩余空间
        const h = el.clientHeight;
        const base = Math.min(w, h > 0 ? h : w, 800);
        setBoardSize(Math.max(220, Math.floor(base)));
      } else {
        const maxByHeight = window.innerHeight - 200;
        setBoardSize(Math.min(w, 800, Math.max(280, maxByHeight)));
      }
    };

    updateBoardSize();
    window.addEventListener('resize', updateBoardSize);
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => updateBoardSize());
      ro.observe(el);
    }
    return () => {
      window.removeEventListener('resize', updateBoardSize);
      if (ro) ro.disconnect();
    };
  }, [fill]);

  // 更新棋子状态
  useEffect(() => {
    setBoardStones(initializeStones());
  }, [stones, size]);

  // 绘制棋盘
  const drawBoard = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 清空画布
    ctx.clearRect(0, 0, boardSize, boardSize);

    // 设置绘制参数
    const padding = boardSize * 0.07;
    const gridSize = (boardSize - padding * 2) / (size - 1);
    const stoneRadius = gridSize * 0.45;
    const lineWidth = 1;

    // 绘制背景
    ctx.fillStyle = '#DCB35C'; // 木质棋盘颜色
    ctx.fillRect(0, 0, boardSize, boardSize);

    // 绘制网格线
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = lineWidth;

    // 横线
    for (let i = 0; i < size; i++) {
      const y = padding + i * gridSize;
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(boardSize - padding, y);
      ctx.stroke();
    }

    // 竖线
    for (let i = 0; i < size; i++) {
      const x = padding + i * gridSize;
      ctx.beginPath();
      ctx.moveTo(x, padding);
      ctx.lineTo(x, boardSize - padding);
      ctx.stroke();
    }

    // 绘制星位
    const starPoints = getStarPoints(size);
    const starRadius = gridSize * 0.1;

    ctx.fillStyle = '#000000';
    starPoints.forEach(([x, y]) => {
      const drawX = padding + x * gridSize;
      const drawY = padding + y * gridSize;
      ctx.beginPath();
      ctx.arc(drawX, drawY, starRadius, 0, Math.PI * 2);
      ctx.fill();
    });

    // 绘制棋子
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const stone = boardStones[y][x];
        if (stone !== StoneColor.EMPTY) {
          const drawX = padding + x * gridSize;
          const drawY = padding + y * gridSize;

          // 创建棋子渐变
          const gradient = ctx.createRadialGradient(
            drawX - stoneRadius * 0.3,
            drawY - stoneRadius * 0.3,
            0,
            drawX,
            drawY,
            stoneRadius
          );

          if (stone === StoneColor.BLACK) {
            gradient.addColorStop(0, '#666666');
            gradient.addColorStop(1, '#000000');
          } else {
            gradient.addColorStop(0, '#FFFFFF');
            gradient.addColorStop(1, '#DDDDDD');
          }

          // 绘制棋子
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(drawX, drawY, stoneRadius, 0, Math.PI * 2);
          ctx.fill();

          // 棋子边框
          ctx.strokeStyle = stone === StoneColor.BLACK ? '#333333' : '#AAAAAA';
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }
    }

    // 高亮最后一步
    if (highlightLastMove && lastMove) {
      const drawX = padding + (lastMove.x - 1) * gridSize;
      const drawY = padding + (lastMove.y - 1) * gridSize;

      ctx.strokeStyle = '#FF0000';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(drawX, drawY, stoneRadius * 0.3, 0, Math.PI * 2);
      ctx.stroke();
    }

    // 绘制死子标记（红色半透明覆盖 + 红圈，区别于蓝色讲解标记）
    if (deadStones && deadStones.length > 0) {
      const deadColor = '#ef4444';
      ctx.lineWidth = Math.max(1.5, gridSize * 0.08);
      ctx.strokeStyle = deadColor;
      for (const dead of deadStones) {
        const drawX = padding + (dead.x - 1) * gridSize;
        const drawY = padding + (dead.y - 1) * gridSize;
        ctx.fillStyle = 'rgba(239, 68, 68, 0.35)';
        ctx.beginPath();
        ctx.arc(drawX, drawY, stoneRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(drawX, drawY, stoneRadius, 0, Math.PI * 2);
        ctx.stroke();
      }
    }

    // 绘制讲解标记
    if (marks && marks.length > 0) {
      const markColor = '#1d4ed8'; // 蓝色，与黑白子、木色棋盘均区分
      ctx.lineWidth = Math.max(1.5, gridSize * 0.08);
      ctx.strokeStyle = markColor;
      ctx.fillStyle = markColor;

      for (const mark of marks) {
        const drawX = padding + (mark.coord.x - 1) * gridSize;
        const drawY = padding + (mark.coord.y - 1) * gridSize;
        const r = stoneRadius * 0.75;

        switch (mark.type) {
          case 'circle':
            ctx.beginPath();
            ctx.arc(drawX, drawY, r, 0, Math.PI * 2);
            ctx.stroke();
            break;
          case 'square':
            ctx.beginPath();
            ctx.strokeRect(drawX - r, drawY - r, r * 2, r * 2);
            break;
          case 'triangle':
            ctx.beginPath();
            ctx.moveTo(drawX, drawY - r);
            ctx.lineTo(drawX - r * 0.9, drawY + r * 0.7);
            ctx.lineTo(drawX + r * 0.9, drawY + r * 0.7);
            ctx.closePath();
            ctx.stroke();
            break;
          case 'cross':
            ctx.beginPath();
            ctx.moveTo(drawX - r, drawY - r);
            ctx.lineTo(drawX + r, drawY + r);
            ctx.moveTo(drawX + r, drawY - r);
            ctx.lineTo(drawX - r, drawY + r);
            ctx.stroke();
            break;
          case 'letter':
            ctx.font = `bold ${gridSize * 0.7}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            // 白色描边提升可读性
            ctx.lineWidth = gridSize * 0.08;
            ctx.strokeStyle = 'rgba(255,255,255,0.9)';
            ctx.strokeText(mark.letter || '', drawX, drawY);
            ctx.fillStyle = markColor;
            ctx.fillText(mark.letter || '', drawX, drawY);
            // 恢复描边样式
            ctx.lineWidth = Math.max(1.5, gridSize * 0.08);
            ctx.strokeStyle = markColor;
            break;
        }
      }
    }

    // 绘制悬停效果
    if (showHover && hoverCoord && !disabled) {
      const drawX = padding + (hoverCoord.x - 1) * gridSize;
      const drawY = padding + (hoverCoord.y - 1) * gridSize;

      ctx.fillStyle = 'rgba(255, 0, 0, 0.2)';
      ctx.beginPath();
      ctx.arc(drawX, drawY, stoneRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    // 绘制落子光标（手机端方向键定位）
    if (cursor) {
      const cx = padding + (cursor.x - 1) * gridSize;
      const cy = padding + (cursor.y - 1) * gridSize;
      const r = stoneRadius * 1.2;
      ctx.save();
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = Math.max(2, gridSize * 0.12);
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.globalAlpha = 0.6;
      ctx.beginPath();
      ctx.moveTo(cx - r * 1.3, cy);
      ctx.lineTo(cx + r * 1.3, cy);
      ctx.moveTo(cx, cy - r * 1.3);
      ctx.lineTo(cx, cy + r * 1.3);
      ctx.stroke();
      ctx.restore();
    }

    // 绘制坐标
    if (showCoordinates) {
      ctx.fillStyle = '#000000';
      ctx.font = `${gridSize * 0.4}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // 列坐标 (A-T, 跳过I)
      for (let i = 0; i < size; i++) {
        const x = padding + i * gridSize;
        const y = padding - gridSize * 0.55;
        const label = getColumnLabel(i);
        ctx.fillText(label, x, y);

        // 底部坐标
        const bottomY = boardSize - padding + gridSize * 0.55;
        ctx.fillText(label, x, bottomY);
      }

      // 行坐标
      for (let i = 0; i < size; i++) {
        const x = padding - gridSize * 0.55;
        const y = padding + i * gridSize;
        ctx.fillText((size - i).toString(), x, y);

        // 右侧坐标
        const rightX = boardSize - padding + gridSize * 0.55;
        ctx.fillText((size - i).toString(), rightX, y);
      }
    }
  }, [boardSize, size, boardStones, highlightLastMove, lastMove, showHover, hoverCoord, disabled, showCoordinates, marks, deadStones, cursor]);

  // 获取星位坐标
  const getStarPoints = (boardSize: number): [number, number][] => {
    const points: [number, number][] = [];

    if (boardSize === 19) {
      const stars = [3, 9, 15];
      for (const x of stars) {
        for (const y of stars) {
          points.push([x, y]);
        }
      }
    } else if (boardSize === 13) {
      const stars = [3, 6, 9];
      for (const x of stars) {
        for (const y of stars) {
          points.push([x, y]);
        }
      }
    } else if (boardSize === 9) {
      const stars = [2, 4, 6];
      for (const x of stars) {
        for (const y of stars) {
          points.push([x, y]);
        }
      }
    }

    return points;
  };

  // 获取列标签 (A-T, 跳过I)
  const getColumnLabel = (index: number): string => {
    // 围棋列坐标跳过字母 I（避免与数字 1 混淆）：A-H、J-T
    const letterIndex = index >= 8 ? index + 1 : index;
    return String.fromCharCode(65 + letterIndex);
  };

  // 处理鼠标移动
  const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!showHover || disabled) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const padding = boardSize * 0.07;
    const gridSize = (boardSize - padding * 2) / (size - 1);

    const boardX = Math.round((x - padding) / gridSize);
    const boardY = Math.round((y - padding) / gridSize);

    if (
      boardX >= 0 &&
      boardX < size &&
      boardY >= 0 &&
      boardY < size
    ) {
      setHoverCoord({ x: boardX + 1, y: boardY + 1 });
    } else {
      setHoverCoord(null);
    }
  };

  // 处理鼠标离开
  const handleMouseLeave = () => {
    setHoverCoord(null);
  };

  // 处理点击
  const handleClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (disabled) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const padding = boardSize * 0.07;
    const gridSize = (boardSize - padding * 2) / (size - 1);

    const boardX = Math.round((x - padding) / gridSize);
    const boardY = Math.round((y - padding) / gridSize);

    if (
      boardX >= 0 &&
      boardX < size &&
      boardY >= 0 &&
      boardY < size
    ) {
      const coord: Coordinate = { x: boardX + 1, y: boardY + 1 };
      // 光标模式（手机端）：点棋盘只移动光标，不直接落子（防误触）
      if (cursorMode) {
        onCursor?.(coord);
        return;
      }
      if (!onMove) return;
      // 已有棋子的位置静默忽略（对局场景：无法落子，不提示）
      if (blockOccupied && boardStones[boardY] && boardStones[boardY][boardX] !== StoneColor.EMPTY) {
        return;
      }
      onMove(coord);
    }
  };

  // 绘制棋盘
  useEffect(() => {
    drawBoard();
  }, [drawBoard]);

  const containerStyle: React.CSSProperties = fill
    ? { flex: 1, minHeight: 0, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }
    : { width: '100%', maxWidth: '800px', margin: '0 auto' };

  return (
    <div ref={containerRef} className="go-board-container" style={containerStyle}>
      <canvas
        ref={canvasRef}
        width={boardSize}
        height={boardSize}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        className="go-board-canvas"
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'block',
          borderRadius: '4px',
          maxWidth: '100%',
          height: 'auto'
        }}
      />

      {hoverCoord && showHover && !disabled && (
        <div className="coord-hint">
          {getColumnLabel(hoverCoord.x - 1)}{size - hoverCoord.y + 1}
        </div>
      )}
    </div>
  );
};

export default GoBoard;
